import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';

import {
  invoicesColumns, invoicesFields,
  usersColumns, usersFields,
  projectsColumns, projectsFields
} from '../data/tableData';
import Loadable from '../layouts/full-layout/loadable/Loadable';
import AdminGuard from '../AdminGuard';
import SuperadminGuard from '../SuperadminGuard';
import ExpiredGuard from '../components/domain/ExpiredGuard';
import CustomerSuccessGuard from '../CustomerSuccessGuard';
import FeatureGuard from '../components/guards/FeatureGuard';
import FLAGS from '../config/featureFlags';


/* ***Layouts**** */
const FullLayout = Loadable(
  lazy(() => import('../layouts/full-layout/FullLayout'))
);
const BlankLayout = Loadable(
  lazy(() => import('../layouts/blank-layout/BlankLayout'))
);
/* ***End Layouts**** */

const Error = Loadable(lazy(() => import('../views/authentication/Error')));
const Login = Loadable(lazy(() => import('../views/authentication/Login')));
const Register = Loadable(
  lazy(() => import('../views/authentication/Register'))
);
const NewPassword = Loadable(
  lazy(() => import('../views/authentication/newPassword2'))
);
const MailSent = Loadable(
  lazy(() => import('../views/authentication/MailSent'))
);

const PasswordUpdated = Loadable(
  lazy(() => import('../views/authentication/PasswordUpdated'))
);
const RequestPasswordReset = Loadable(
  lazy(() => import('../views/authentication/RequestPasswordReset'))
);
const VerifyEmail = Loadable(
  lazy(() => import('../pages/auth/VerifyEmail'))
);
const AcceptInvite = Loadable(
  lazy(() => import('../pages/auth/AcceptInvite'))
);

const LeadsProjectMainPage = Loadable(
  lazy(() => import('../pages/leads/LeadsProjectMainPage'))
);
const ConfigLeads = Loadable(
  lazy(() => import('../components/leads/ConfigLeads'))
);
const CollectionsProjectMainPage = Loadable(
  lazy(() => import('../pages/collections/CollectionsProjectMainPage'))
);
const ConfigProjectsPage = Loadable(
  lazy(() => import('../pages/config/ConfigProjectPage'))
);
const ConfigCollections = Loadable(
  lazy(() => import('../components/collections/ConfigCollections'))
);
const ConfigUsers = Loadable(
  lazy(() => import('../pages/users/ConfigUsers'))
);
const ProjectEmailStatsPage = Loadable(
  lazy(() => import('../pages/projects/ProjectEmailStatsPage'))
);
const Dashboard = Loadable(
  lazy(() => import('../views/dashboards/Dashboard'))
);
const SchedulerPage = Loadable(
  lazy(() => import('../pages/admin/SchedulerPage'))
);
const AdminScrapersPage = Loadable(
  lazy(() => import('../pages/admin/AdminScrapersPage'))
);
const CompanyScrapersPage = Loadable(
  lazy(() => import('../pages/company/ScrapersPage'))
);
const CompanyIntegrationsPage = Loadable(
  lazy(() => import('../pages/company/IntegrationsPage'))
);
const SendgridEmailValidationPage = Loadable(
  lazy(() => import('../pages/admin/SendgridEmailValidationPage'))
);
const EcommerceIntegrationsPage = Loadable(
  lazy(() => import('../pages/admin/EcommerceIntegrationsPage'))
);
const APIKeysPage = Loadable(
  lazy(() => import('../pages/admin/APIKeysPage'))
);
const ShopifyBillingReportPage = Loadable(
  lazy(() => import('../pages/admin/ShopifyBillingReportPage'))
);
const ImagesPage = Loadable(
  lazy(() => import('../pages/admin/ImagesPage'))
);
const AnalyticsDashboard = Loadable(
  lazy(() => import('../pages/analytics/AnalyticsDashboard'))
);
const MarketingAnalyticsDashboard = Loadable(
  lazy(() => import('../pages/analytics/MarketingAnalyticsDashboard'))
);
const AIProposalsPage = Loadable(
  lazy(() => import('../pages/ai-proposals/AIProposalsPage'))
);
const AdsPage = Loadable(
  lazy(() => import('../pages/ads/AdsPage'))
);
const CampaignDetailPage = Loadable(
  lazy(() => import('../pages/ads/CampaignDetailPage'))
);
const ProductsPage = Loadable(
  lazy(() => import('../pages/products/ProductsPage'))
);
const ContactsPage = Loadable(
  lazy(() => import('../pages/config/ContactsPage'))
);
const OnboardingSettingsPage = Loadable(
  lazy(() => import('../pages/settings/OnboardingSettingsPage'))
);

const LogsView = Loadable(lazy(() => import('../views/logs/LogsView')));

const Status = Loadable(lazy(() => import('../views/status/Status')));

/* ****Tables***** */
const BasicTable = Loadable(lazy(() => import('../views/tables/BasicTable')));
const PaginationTable = Loadable(
  lazy(() => import('../views/tables/PaginationTable'))
);
const EnhancedTable = Loadable(
  lazy(() => import('../views/tables/EnhancedTable'))
);
const CollapsibleTable = Loadable(
  lazy(() => import('../views/tables/CollapsibleTable'))
);
const FixedHeaderTable = Loadable(
  lazy(() => import('../views/tables/FixedHeaderTable'))
);
const TokensTable = Loadable(lazy(() => import('../views/tokens/Tokens')));
const TokenView = Loadable(lazy(() => import('../views/tokens/TokenView')));
const QuotesTable = Loadable(
  lazy(() => import('../views/billing/QuotesTable'))
);
const InvoicesTable = Loadable(
  lazy(() => import('../views/billing/InvoicesTable'))
);
const InvoiceView = Loadable(
  lazy(() => import('../views/billing/InvoiceView'))
);
const QuoteView = Loadable(lazy(() => import('../views/billing/QuoteView')));
const PaymentsView = Loadable(
  lazy(() => import('../views/payments/PaymentsListView'))
);
const PaymentsSettings = Loadable(
  lazy(() => import('../views/payments/PaymentWidgetList'))
);
const PaymentWidgetDetails = Loadable(
  lazy(() => import('../views/payments/WidgetDetails'))
);
// form elements
const ExAutoComplete = Loadable(
  lazy(() => import('../views/form-elements/ExAutoComplete'))
);
const ExButton = Loadable(
  lazy(() => import('../views/form-elements/ExButton'))
);
const ExCheckbox = Loadable(
  lazy(() => import('../views/form-elements/ExCheckbox'))
);
const ExDateTime = Loadable(
  lazy(() => import('../views/form-elements/ExDateTime'))
);
const ExRadio = Loadable(lazy(() => import('../views/form-elements/ExRadio')));
const ExSlider = Loadable(
  lazy(() => import('../views/form-elements/ExSlider'))
);
const ExSwitch = Loadable(
  lazy(() => import('../views/form-elements/ExSwitch'))
);
const FormWizard = Loadable(
  lazy(() => import('../views/form-layouts/FormWizard'))
);
// form layouts
const FormLayouts = Loadable(
  lazy(() => import('../views/form-layouts/FormLayouts'))
);
const FormCustom = Loadable(
  lazy(() => import('../views/form-layouts/FormCustom'))
);

// userprofile
const UserProfile = Loadable(
  lazy(() => import('../views/user-profile/UserProfile'))
);
const UserNewPassword = Loadable(
  lazy(() => import('../views/user-profile/UserNewPassword'))
);
const CompanyProfile = Loadable(
  // lazy(() => import('../views/user-profile/CompanyProfile'))
  lazy(() => import('../pages/company/CompanySettings'))
);
const CompanyUser = Loadable(
  lazy(() => import('../views/user-profile/CompanyUserUpdate'))
);
const CompanyUserCreate = Loadable(
  lazy(() => import('../views/user-profile/CompanyUserCreate'))
);

// chart
const LineChart = Loadable(lazy(() => import('../views/charts/LineChart')));
const GredientChart = Loadable(
  lazy(() => import('../views/charts/GredientChart'))
);
const DoughnutChart = Loadable(
  lazy(() => import('../views/charts/DoughnutChart'))
);
const AreaChart = Loadable(lazy(() => import('../views/charts/AreaChart')));
const ColumnChart = Loadable(lazy(() => import('../views/charts/ColumnChart')));
const CandlestickChart = Loadable(
  lazy(() => import('../views/charts/CandlestickChart'))
);
const RadialbarChart = Loadable(
  lazy(() => import('../views/charts/RadialbarChart'))
);

// icons
const ReactIcons = Loadable(lazy(() => import('../views/icons/ReactIcons')));

// Alert
const ExAlert = Loadable(lazy(() => import('../views/alert/ExAlert')));

const EditableTable = Loadable(lazy(() => import('../components/EditableTable')));
const ClientsPage = Loadable(lazy(() => import('../pages/admin/ClientsPage')));
const CollectionsPage = Loadable(lazy(() => import('../pages/admin/CollectionsPage')));
const PlansPage = Loadable(lazy(() => import('../pages/admin/PlansPage')));
const SubscriptionsPage = Loadable(lazy(() => import('../pages/admin/SubscriptionsPage')));

const ContactsTablePage = Loadable(lazy(() => import('../pages/contacts/ContactsTablePage')));
const CreateSegmentPage = Loadable(lazy(() => import('../pages/contacts/CreateSegmentPage')));
const ManageSegmentsPage = Loadable(lazy(() => import('../pages/contacts/ManageSegmentsPage')));
const AiSegmentSuggestionsPage = Loadable(lazy(() => import('../pages/contacts/AiSegmentSuggestionsPage')));
const ProjectSettingsPage = Loadable(lazy(() => import('../pages/projects/ProjectSettingsPage')));
const PlanUsagePage = Loadable(lazy(() => import('../pages/company/PlanUsagePage')));
const EmailConfigPage = Loadable(lazy(() => import('../pages/company/EmailConfigPage')));
const DomainSettingsPage = Loadable(lazy(() => import('../pages/company/DomainSettingsPage')));
const BrandKitPage = Loadable(lazy(() => import('../pages/company/BrandKitPage')));

const AutomationsListPage = FLAGS.automations
  ? Loadable(lazy(() => import('../pages/automations/AutomationsListPage')))
  : null;
const FlowBuilderPage = FLAGS.automations
  ? Loadable(lazy(() => import('../pages/automations/FlowBuilderPage')))
  : null;
const AutomationsAnalyticsPage = FLAGS.automations
  ? Loadable(lazy(() => import('../pages/automations/AutomationsAnalyticsPage')))
  : null;
const FlowAnalyticsPage = FLAGS.automations
  ? Loadable(lazy(() => import('../pages/automations/FlowAnalyticsPage')))
  : null;

const NotificationsPage = Loadable(
  lazy(() => import('../pages/notifications/NotificationsPage'))
);

const PlansSelectionPage = Loadable(
  lazy(() => import('../pages/plans/PlansSelectionPage'))
);

// Billing Pages
const BillingDashboardPage = Loadable(
  lazy(() => import('../pages/billing/BillingDashboardPage'))
);
const BillsPage = Loadable(
  lazy(() => import('../pages/billing/BillsPage'))
);
const BillDetailPage = Loadable(
  lazy(() => import('../pages/billing/BillDetailPage'))
);
const PaymentsPage = Loadable(
  lazy(() => import('../pages/billing/PaymentsPage'))
);
const PaymentSuccessPage = Loadable(
  lazy(() => import('../pages/billing/PaymentSuccessPage'))
);
const PaymentFailurePage = Loadable(
  lazy(() => import('../pages/billing/PaymentFailurePage'))
);
const PaymentPendingPage = Loadable(
  lazy(() => import('../pages/billing/PaymentPendingPage'))
);


/* ****Routes***** */



const Router = [
  {
    path: '/',
    element: <FullLayout />,
    children: [
      { path: '/', element: <ExpiredGuard><Dashboard /></ExpiredGuard> },
      { path: '/cobranza/:projectId', exact: true, element: <ExpiredGuard><CollectionsProjectMainPage /></ExpiredGuard> },
      { path: '/projects/:projectId', exact: true, element: <ExpiredGuard><LeadsProjectMainPage /></ExpiredGuard> },
      { path: '/tables/basic-table', element: <BasicTable /> },
      { path: '/tables/pagination-table', element: <PaginationTable /> },
      { path: '/tables/enhanced-table', element: <EnhancedTable /> },
      { path: '/tables/collapsible-table', element: <CollapsibleTable /> },
      { path: '/tables/fixed-header-table', element: <FixedHeaderTable /> },
      { path: '/form-layouts/form-layouts', element: <FormLayouts /> },
      { path: '/form-elements/autocomplete', element: <ExAutoComplete /> },
      { path: '/form-elements/button', element: <ExButton /> },
      { path: '/form-elements/checkbox', element: <ExCheckbox /> },
      { path: '/form-elements/date-time', element: <ExDateTime /> },
      { path: '/form-elements/radio', element: <ExRadio /> },
      { path: '/form-elements/slider', element: <ExSlider /> },
      { path: '/form-elements/switch', element: <ExSwitch /> },
      { path: '/form-layouts/form-wizard', element: <FormWizard /> },
      { path: '/user-profile', element: <UserProfile /> },
      { path: '/charts/line-chart', element: <LineChart /> },
      { path: '/charts/gredient-chart', element: <GredientChart /> },
      { path: '/charts/doughnut-pie-chart', element: <DoughnutChart /> },
      { path: '/charts/area-chart', element: <AreaChart /> },
      { path: '/charts/column-chart', element: <ColumnChart /> },
      { path: '/charts/candlestick-chart', element: <CandlestickChart /> },
      { path: '/charts/radialbar-chart', element: <RadialbarChart /> },
      { path: '/react-icons', element: <ReactIcons /> },
      { path: '/form-layouts/form-custom', element: <FormCustom /> },
      // { path: '/quill-editor', element: <QuillEditor /> },
      { path: '/alert', element: <ExAlert /> },
      { path: '/tokens', element: <ExpiredGuard><TokensTable /></ExpiredGuard> },
      { path: '/tokens/:id', element: <ExpiredGuard><TokenView /></ExpiredGuard> },
      { path: '/logs', element: <ExpiredGuard><LogsView /></ExpiredGuard> },
      { path: '/billing/quotes', element: <QuotesTable /> },
      { path: '/billing/quote/:id', element: <QuoteView /> },
      { path: '/billing/invoices', element: <InvoicesTable /> },
      { path: '/billing/invoice/:id', element: <InvoiceView /> },
      // Client Billing Routes
      { path: '/billing/dashboard', exact: true, element: <AdminGuard><BillingDashboardPage /></AdminGuard> },
      { path: '/billing/bills', exact: true, element: <AdminGuard><BillsPage /></AdminGuard> },
      { path: '/billing/bills/:billId', exact: true, element: <AdminGuard><BillDetailPage /></AdminGuard> },
      { path: '/billing/payments', exact: true, element: <AdminGuard><PaymentsPage /></AdminGuard> },
      // Payment result pages
      { path: '/payment/success', exact: true, element: <PaymentSuccessPage /> },
      { path: '/payment/failure', exact: true, element: <PaymentFailurePage /> },
      { path: '/payment/pending', exact: true, element: <PaymentPendingPage /> },
      { path: '/settings/company', element: <AdminGuard><CompanyProfile /></AdminGuard> },
      { path: '/settings/brand-kit', element: <AdminGuard><BrandKitPage /></AdminGuard> },
      { path: '/settings/company/user', element: <AdminGuard><CompanyUser /></AdminGuard> },
      { path: '/settings/company/user/create', element: <AdminGuard><CompanyUserCreate /></AdminGuard> },
      { path: '/settings/user', element: <UserProfile /> },
      { path: '/settings/user/new-password', element: <UserNewPassword /> },
      { path: '/settings/scrapers', element: <ExpiredGuard><AdminGuard><CompanyScrapersPage /></AdminGuard></ExpiredGuard> },
      { path: '/settings/integrations', element: <ExpiredGuard><AdminGuard><CompanyIntegrationsPage /></AdminGuard></ExpiredGuard> },
      { path: '/settings/users', exact:true, element: <AdminGuard><ConfigUsers /></AdminGuard> },
      { path: '/payments', exact: true, element: <ExpiredGuard><PaymentsView /></ExpiredGuard> },
      { path: '/payments/widgets', exact: true, element: <ExpiredGuard><PaymentsSettings /></ExpiredGuard> },
      { path: '/payments/widgets/:id', exact: true, element: <ExpiredGuard><PaymentWidgetDetails /></ExpiredGuard> },
      { path: '/status', exact: true, element: <ExpiredGuard><Status /></ExpiredGuard> },
      { path: '/admin/clients', exact: true, element: <ExpiredGuard><CustomerSuccessGuard><ClientsPage /></CustomerSuccessGuard></ExpiredGuard> },
      { path: '/admin/users', exact: true, element: <ExpiredGuard><CustomerSuccessGuard><EditableTable endpoint="users" columns={usersColumns} fields={usersFields} /></CustomerSuccessGuard></ExpiredGuard> },
      { path: '/admin/invoices', exact: true, element: <ExpiredGuard><CustomerSuccessGuard><EditableTable endpoint="invoices" columns={invoicesColumns} fields={invoicesFields} /></CustomerSuccessGuard></ExpiredGuard> },
      { path: '/admin/plans', exact: true, element: <SuperadminGuard><PlansPage /></SuperadminGuard> },
      { path: '/admin/subscriptions', exact: true, element: <SuperadminGuard><SubscriptionsPage /></SuperadminGuard> },
      { path: '/admin/collections', exact: true, element: <SuperadminGuard><CollectionsPage /></SuperadminGuard> },
      { path: '/admin/projects', exact: true, element: <SuperadminGuard><EditableTable endpoint="projects" columns={projectsColumns} fields={projectsFields} /></SuperadminGuard> },
      { path: '/admin/scrapers', element: <SuperadminGuard><AdminScrapersPage /></SuperadminGuard> },
      { path: '/admin/scheduler', exact: true, element: <SuperadminGuard><SchedulerPage /></SuperadminGuard> },
      { path: '/admin/sendgrid-email-validation', exact: true, element: <SuperadminGuard><SendgridEmailValidationPage /></SuperadminGuard> },
      { path: '/admin/ecommerce-integrations', exact: true, element: <SuperadminGuard><EcommerceIntegrationsPage /></SuperadminGuard> },
      { path: '/admin/api-keys', exact: true, element: <SuperadminGuard><APIKeysPage /></SuperadminGuard> },
      { path: '/admin/shopify-billing-report', exact: true, element: <SuperadminGuard><ShopifyBillingReportPage /></SuperadminGuard> },
      { path: '/settings/images', exact: true, element: <ExpiredGuard><AdminGuard><ImagesPage /></AdminGuard></ExpiredGuard> },
      { path: '/analytics', exact: true, element: <ExpiredGuard><AdminGuard><AnalyticsDashboard /></AdminGuard></ExpiredGuard> },
      { path: '/analytics/marketing', exact: true, element: <ExpiredGuard><AdminGuard><MarketingAnalyticsDashboard /></AdminGuard></ExpiredGuard> },
      { path: '/ai-proposals', exact: true, element: <ExpiredGuard><AIProposalsPage /></ExpiredGuard> },
      { path: '/ads', exact: true, element: <ExpiredGuard><AdminGuard><FeatureGuard feature="ads"><AdsPage /></FeatureGuard></AdminGuard></ExpiredGuard> },
      { path: '/ads/:integrationId/campaigns/:campaignExternalId', exact: true, element: <ExpiredGuard><AdminGuard><FeatureGuard feature="ads"><CampaignDetailPage /></FeatureGuard></AdminGuard></ExpiredGuard> },
      { path: '/products', exact: true, element: <ExpiredGuard><ProductsPage /></ExpiredGuard> },
      ...(FLAGS.automations ? [
        { path: '/automations', exact: true, element: <ExpiredGuard><AdminGuard><FeatureGuard feature="automations"><AutomationsListPage /></FeatureGuard></AdminGuard></ExpiredGuard> },
        { path: '/automations/new', exact: true, element: <ExpiredGuard><AdminGuard><FeatureGuard feature="automations"><FlowBuilderPage /></FeatureGuard></AdminGuard></ExpiredGuard> },
        // Static '/automations/analytics' (general dashboard) MUST come before
        // the dynamic '/automations/:flowId' so the flowId param doesn't
        // capture it. React Router v6 (useRoutes) ranks static segments above
        // params, so the static route wins regardless; the ordering is belt
        // and suspenders. The per-flow '/automations/:flowId/analytics' is
        // unambiguous (different depth) and never conflicts with the general one.
        { path: '/automations/analytics', exact: true, element: <ExpiredGuard><AdminGuard><FeatureGuard feature="automations"><AutomationsAnalyticsPage /></FeatureGuard></AdminGuard></ExpiredGuard> },
        { path: '/automations/:flowId', exact: true, element: <ExpiredGuard><AdminGuard><FeatureGuard feature="automations"><FlowBuilderPage /></FeatureGuard></AdminGuard></ExpiredGuard> },
        { path: '/automations/:flowId/analytics', exact: true, element: <ExpiredGuard><AdminGuard><FeatureGuard feature="automations"><FlowAnalyticsPage /></FeatureGuard></AdminGuard></ExpiredGuard> },
      ] : []),
      { path: '/contacts', exact: true, element: <ExpiredGuard><ContactsTablePage /></ExpiredGuard> },
      { path: '/contacts/segments', exact: true, element: <ExpiredGuard><ManageSegmentsPage /></ExpiredGuard> },
      { path: '/contacts/ai-suggestions', exact: true, element: <ExpiredGuard><AiSegmentSuggestionsPage /></ExpiredGuard> },
      { path: '/contacts/create-segment', exact: true, element: <ExpiredGuard><CreateSegmentPage /></ExpiredGuard> },
      { path: '/settings/projects/:projectId', exact: true, element: <ExpiredGuard><AdminGuard><ProjectSettingsPage /></AdminGuard></ExpiredGuard> },
      { path: '/settings/projects/:projectId/email-stats', exact: true, element: <ExpiredGuard><AdminGuard><ProjectEmailStatsPage /></AdminGuard></ExpiredGuard> },
      { path: '/settings/projects', exact: true, element: <ExpiredGuard><AdminGuard><ConfigProjectsPage /></AdminGuard></ExpiredGuard> },
      { path: '/settings/plan-usage', exact: true, element: <AdminGuard><PlanUsagePage /></AdminGuard> },
      { path: '/settings/email', exact: true, element: <ExpiredGuard><AdminGuard><EmailConfigPage /></AdminGuard></ExpiredGuard> },
      { path: '/settings/domain', exact: true, element: <ExpiredGuard><AdminGuard><DomainSettingsPage /></AdminGuard></ExpiredGuard> },
      { path: '/settings/onboarding', exact: true, element: <ExpiredGuard><OnboardingSettingsPage /></ExpiredGuard> },
      { path: '/settings/collections', exact: true, element: <ExpiredGuard><ConfigCollections /></ExpiredGuard> },
      { path: '/settings/leads', exact: true, element: <ExpiredGuard><ConfigLeads /></ExpiredGuard> },
      { path: '/settings/contacts', exact: true, element: <ExpiredGuard><ContactsPage /></ExpiredGuard> },
      { path: '/plans', exact: true, element: <PlansSelectionPage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '*', element: <Navigate to='/auth/404' replace /> },
    ],
  },
  {
    path: '/auth',
    element: <BlankLayout />,
    children: [
      { path: '/auth/404', element: <Error /> },
      { path: '/auth/login', element: <Login /> },
      { path: '/auth/register', element: <Register /> },
      // { path: '/auth/reset-password', element: <ResetPassword /> },
      { path: '/auth/new-password', element: <NewPassword /> },
      // { path: '/auth/new-password/:token', element: <NewPassword />},
      { path: '/auth/request-password-reset', element: <RequestPasswordReset /> },
      { path: '/auth/password-updated', element: <PasswordUpdated /> },
      { path: '/auth/mail-sent', element: <MailSent /> },
      { path: '/auth/verify-email', element: <VerifyEmail /> },
      { path: '/auth/accept-invite', element: <AcceptInvite /> },
      { path: '/auth/quickdownload', element: <Register /> },
      { path: '*', element: <Navigate to='/auth/404' replace /> },
    ],
  },
  {
    // add single rout that does not need to be authenticated nor have a layout
    path: '/quickdownload',
    element: <BlankLayout />,
    children: [
      { path: '*', element: <Navigate to='/auth/404' replace /> },
    ],
  },
  {
    path: '/download',
    element: <BlankLayout />,
    children: [
      { path: '/download/billing/invoice/:id', element: <InvoiceView /> },
      { path: '/download/billing/quote/:id', element: <QuoteView /> },
    ],

  }
];

export default Router;
